import { toast } from "../utils/toastsMessage";
import axiosInstance from "../services/axiosService";

let cachedLimits: {
  maxImageUploadMb: number;
  maxVideoUploadMb: number;
  maxFileUploadMb: number;
  maxVideoDurationSeconds?: number;
  trustedMediaUrlPrefixes?: string[];
} | null = null;
export const getUploadLimits = async () => {
  if (cachedLimits) return cachedLimits;
  const res = await axiosInstance.get(`/api/v1/upload/config`);
  if (!res.data?.success || !res.data?.data) {
    throw new Error("Failed to load upload limits");
  }
  cachedLimits = {
    maxImageUploadMb: Number(res.data.data.maxImageUploadMb),
    maxVideoUploadMb: Number(res.data.data.maxVideoUploadMb),
    maxFileUploadMb: Number(res.data.data.maxFileUploadMb),
    maxVideoDurationSeconds: typeof res.data.data.maxVideoDurationSeconds === "number" ? Number(res.data.data.maxVideoDurationSeconds) : undefined,
    trustedMediaUrlPrefixes: Array.isArray(res.data.data.trustedMediaUrlPrefixes) ? res.data.data.trustedMediaUrlPrefixes : undefined,
  };
  return cachedLimits;
};

const isTrustedUploadedUrl = async (url: string) => {
  const limits = await getUploadLimits();
  const prefixes = Array.isArray(limits.trustedMediaUrlPrefixes) ? limits.trustedMediaUrlPrefixes.filter(Boolean) : [];
  if (!prefixes.length) return false;
  return prefixes.some((p) => url.startsWith(p));
};

const inferExtensionFromContentType = (contentType: string): string => {
  const ct = String(contentType || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  const subtype = ct.includes("/") ? ct.split("/")[1] : "";
  if (!subtype) return "bin";

  if (subtype === "jpeg" || subtype === "pjpeg") return "jpg";
  if (subtype === "png" || subtype === "gif" || subtype === "webp" || subtype === "bmp") return subtype;
  if (subtype === "heic" || subtype === "heif") return subtype;
  if (subtype === "quicktime") return "mov";
  if (subtype === "x-msvideo") return "avi";
  if (subtype === "x-ms-wmv") return "wmv";
  if (subtype === "x-flv") return "flv";
  if (subtype === "mpeg") return "mpeg";
  if (subtype === "mp4" || subtype === "webm" || subtype === "m4v") return subtype;

  return "bin";
};

const ensureFileName = (blob: Blob, contentType: string, fileName?: string) => {
  if (typeof fileName === "string" && fileName.trim()) return fileName;
  const ext = inferExtensionFromContentType(contentType);
  return `upload-${Date.now()}.${ext}`;
};

const uploadBlob = async (blob: Blob, contentType: string, fileName?: string) => {
  const limits = await getUploadLimits();
  if (contentType.startsWith("image/") && blob.size > limits.maxImageUploadMb * 1024 * 1024) {
    toast.error(`Image too large. Max ${limits.maxImageUploadMb} MB.`);
    throw new Error("Image exceeds max size");
  }
  if (contentType.startsWith("video/") && blob.size > limits.maxVideoUploadMb * 1024 * 1024) {
    toast.error(`Video too large. Max ${limits.maxVideoUploadMb} MB.`);
    throw new Error("Video exceeds max size");
  }
  if (!contentType.startsWith("image/") && !contentType.startsWith("video/") && blob.size > limits.maxFileUploadMb * 1024 * 1024) {
    toast.error(`File too large. Max ${limits.maxFileUploadMb} MB.`);
    throw new Error("File exceeds max size");
  }

  const safeFileName = ensureFileName(blob, contentType, fileName);
  const presign = await axiosInstance.post(`/api/v1/upload/presign`, {
    contentType,
    fileName: safeFileName,
    userId: localStorage.getItem("userId") || "anonymous",
    folder: "uploads",
    contentLength: blob.size,
  });

  if (!presign.data?.success) {
    throw new Error(presign.data?.message || "Failed to get presigned URL");
  }

  // Support both PUT and POST styles based on backend response shape
  if (presign.data.data.uploadUrl) {
    const { uploadUrl, publicUrl } = presign.data.data;
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: blob,
    });
    if (!putRes.ok) {
      throw new Error(`S3 upload failed with status ${putRes.status}`);
    }
    return publicUrl;
  } else {
    const { url, fields, publicUrl } = presign.data.data;
    const formData = new FormData();
    Object.keys(fields || {}).forEach((k) => formData.append(k, (fields as any)[k]));
    formData.append("file", blob);
    const postRes = await fetch(url, {
      method: "POST",
      body: formData,
    });
    if (!postRes.ok) {
      throw new Error(`S3 upload failed with status ${postRes.status}`);
    }
    return publicUrl;
  }
};

/**
 * Upload an image Blob/File using the presigned upload flow.
 * @returns public URL of the uploaded file
 */
export const uploadImage = async (imageBuffer: any) => {
  try {
    if (typeof imageBuffer === "string" && imageBuffer.startsWith("http")) {
      if (await isTrustedUploadedUrl(imageBuffer)) return imageBuffer;
      throw new Error("Untrusted media URL. Please upload the file instead of linking to an external URL.");
    }

    if (imageBuffer instanceof Blob) {
      const contentType = (imageBuffer as any).type || "application/octet-stream";
      const fileName = ensureFileName(imageBuffer, contentType, (imageBuffer as any).name);
      return await uploadBlob(imageBuffer, contentType, fileName);
    }

    throw new Error("Data URL uploads are not supported");
  } catch (error: any) {
    console.error("Upload image error:", error);
    throw error;
  }
};

/**
 * Upload a video Blob/File using the presigned upload flow.
 * @returns public URL of the uploaded file
 */
export const uploadVideo = async (videoBuffer: any) => {
  try {
    if (typeof videoBuffer === "string" && videoBuffer.startsWith("http")) {
      if (await isTrustedUploadedUrl(videoBuffer)) return videoBuffer;
      throw new Error("Untrusted media URL. Please upload the file instead of linking to an external URL.");
    }

    if (videoBuffer instanceof Blob) {
      const contentType = (videoBuffer as any).type || "application/octet-stream";
      const fileName = ensureFileName(videoBuffer, contentType, (videoBuffer as any).name);
      return await uploadBlob(videoBuffer, contentType, fileName);
    }

    throw new Error("Data URL uploads are not supported");
  } catch (error: any) {
    console.error("Upload video error:", error);
    throw error;
  }
};

/**
 * Upload any Blob/File using the presigned upload flow.
 * @returns public URL of the uploaded file
 */
export const uploadFiles = async (file: any) => {
  try {
    if (typeof file === "string" && file.startsWith("http")) {
      if (await isTrustedUploadedUrl(file)) return file;
      throw new Error("Untrusted media URL. Please upload the file instead of linking to an external URL.");
    }

    if (file instanceof Blob) {
      if (file.size <= 0) {
        throw new Error("File is empty or unreadable. Please try again.");
      }
      const contentType = (file as any).type || "application/octet-stream";
      const fileName = ensureFileName(file, contentType, (file as any).name);
      return await uploadBlob(file, contentType, fileName);
    }

    throw new Error("Data URL uploads are not supported");
  } catch (error: any) {
    console.error("Upload file error:", error);
    throw error;
  }
};
